/**
 * React to a notification ARRIVING, not to the notification list itself.
 *
 * Notifications already reach the browser in realtime, so anything that a
 * notification announces can be picked up the moment it happens instead of on
 * the next poll. cdb_webos#675 is the case that motivated this: after a quota
 * grant the user was unblocked server-side but their composer kept refusing to
 * send until the sidebar's 5-minute usage poll came round.
 *
 * Only NEW arrivals fire. The notifications already present on mount are
 * recorded as seen and ignored — otherwise every mount would replay an old
 * decision and refetch for nothing.
 */

import { useEffect, useRef } from 'react'
import { useNotificationsContextOptional } from '@/context/NotificationsContext'
import type { Notification } from '@/services/notification-service'

export function useNotificationSignal(
  type: Notification['type'],
  onSignal: () => void,
): void {
  const ctx = useNotificationsContextOptional()
  const notifications = ctx?.notifications
  const seenRef = useRef<string | null>(null)
  const initialisedRef = useRef(false)
  // Kept in a ref so a caller passing an inline arrow does not re-run this.
  const handlerRef = useRef(onSignal)
  handlerRef.current = onSignal

  useEffect(() => {
    // No provider (or none loaded yet): the caller keeps whatever polling it
    // has. This hook is an accelerator, never the only path.
    if (!notifications) return

    const latest = notifications.find((n) => n.type === type)?.id ?? null
    if (!initialisedRef.current) {
      initialisedRef.current = true
      seenRef.current = latest
      return
    }
    if (latest && latest !== seenRef.current) {
      seenRef.current = latest
      handlerRef.current()
    }
  }, [notifications, type])
}
